import React, { useCallback, useEffect, useState, useRef } from "react";
import { IoAdd, IoSearch, IoChevronDown } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import Card from "components/card";
import { staffAPI } from "services/staffService";
import { usePermission } from "hooks/usePermission";
import StaffTable from "./components/StaffTable";

const StaffManagement = () => {
  const navigate = useNavigate();
  const { can } = usePermission();
  
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
  });

  const hasInitialFetchedRef = useRef(false);

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  const fetchStaff = useCallback(async () => {
    setLoading(true);

    try {
      const response = await staffAPI.getAllStaff(page, limit, filters);
      const staff = Array.isArray(response?.data) ? response.data : response?.data?.data || [];
      const totalCount = response?.total ?? response?.data?.total ?? 0;

      const normalizedStaff = staff.map((member) => ({
        ...member,
        id: member._id || member.id,
      }));

      setTableData(normalizedStaff);
      setTotal(totalCount);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Failed to fetch staff";
      showToast(message, "error");
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!hasInitialFetchedRef.current) {
      hasInitialFetchedRef.current = true;
      fetchStaff();
    }
  }, []);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    if (hasInitialFetchedRef.current) {
      fetchStaff();
    }
  }, [page, limit, filters, fetchStaff]);

  const handleSearch = (searchValue) => {
    setFilters((prev) => ({ ...prev, search: searchValue }));
  };

  const handleStatusFilter = (status) => {
    setFilters((prev) => ({ ...prev, status }));
  };

  const canEditOrManage = can("staff_management", "update");
  const canDeleteStaff = can("staff_management", "delete");

  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);

  const handleEdit = (staffId) => {
    if (!canEditOrManage) {
      showToast("You don't have permission to edit staff", "error");
      return;
    }
    navigate(`/admin/staff-management/edit/${staffId}`);
  };

  const handlePermissions = (staffId) => {
    if (!canEditOrManage) {
      showToast("You don't have permission to manage permissions", "error");
      return;
    }
    navigate(`/admin/staff-management/permissions/${staffId}`);
  };

  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null, name: "" });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDelete = (staffId, staffName) => {
    if (!canDeleteStaff) {
      showToast("You don't have permission to delete staff", "error");
      return;
    }
    setDeleteConfirm({ show: true, id: staffId, name: staffName });
  };

  const handleCancelDelete = () => {
    setDeleteConfirm({ show: false, id: null, name: "" });
    setDeleteLoading(false);
  };

  const handleConfirmDelete = async () => {
    const { id } = deleteConfirm;
    if (!id) return;
    try {
      setDeleteLoading(true);
      await staffAPI.deleteStaff(id);
      showToast("Staff member deleted successfully", "success");
      setPage(1);
      await fetchStaff();
      handleCancelDelete();
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to delete staff";
      showToast(message, "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleAddStaff = () => {
    if (!can("staff_management", "create")) {
      showToast("You don't have permission to create staff", "error");
      return;
    }
    navigate("/admin/staff-management/add");
  };

  return (
    <div className="mt-3 flex h-full w-full flex-col gap-5">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white">Staff Management</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Manage your team members and their permissions
          </p>
        </div>
        {can("staff_management", "create") && (
          <button
            onClick={handleAddStaff}
            className="flex items-center gap-2 rounded-xl bg-brand-500 px-4 py-3 font-medium text-white transition-all hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-500"
          >
            <IoAdd className="h-5 w-5" />
            Add Staff
          </button>
        )}
      </div>

      {/* Filters Card */}
      <Card extra="p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-end">
          {/* Search */}
          <div className="flex-1">
            <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
              Search
            </label>
            <div className="relative flex items-center">
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
                className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 pl-10 text-sm outline-none placeholder:text-gray-400 dark:border-white/10 dark:bg-navy-700 dark:text-white dark:placeholder:text-gray-500"
              />
              <IoSearch className="absolute left-3 h-4 w-4 text-gray-400" />
            </div>
          </div>

          {/* Status Filter */}
          <div className="md:w-40">
            <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
              Status
            </label>
            <div ref={statusDropdownRef} className="relative mt-2 w-full">
              <button
                type="button"
                onClick={() => setStatusDropdownOpen((s) => !s)}
                className="flex h-12 w-full items-center justify-between rounded-xl border px-3 text-left text-sm border-gray-200 text-gray-700 hover:border-gray-300 dark:border-white/10 dark:text-white dark:hover:border-white/20"
              >
                <span className="truncate">{filters.status === "" ? "All Status" : filters.status === "active" ? "Active" : filters.status === "in-active" ? "Inactive" : filters.status}</span>
                <IoChevronDown className="h-4 w-4 text-gray-500" />
              </button>

              {statusDropdownOpen && (
                <div className="absolute top-full z-50 mt-2 w-full rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-600 dark:bg-gray-800">
                  <button
                    type="button"
                    onClick={() => { handleStatusFilter(""); setStatusDropdownOpen(false); }}
                    className={`h-10 w-full rounded-md px-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${filters.status === "" ? "bg-blue-50 text-blue-600" : "text-gray-700 dark:text-gray-200"}`}
                  >
                    All Status
                  </button>
                  <button
                    type="button"
                    onClick={() => { handleStatusFilter("active"); setStatusDropdownOpen(false); }}
                    className={`h-10 w-full rounded-md px-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${filters.status === "active" ? "bg-blue-50 text-blue-600" : "text-gray-700 dark:text-gray-200"}`}
                  >
                    Active
                  </button>
                  <button
                    type="button"
                    onClick={() => { handleStatusFilter("in-active"); setStatusDropdownOpen(false); }}
                    className={`h-10 w-full rounded-md px-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${filters.status === "in-active" ? "bg-blue-50 text-blue-600" : "text-gray-700 dark:text-gray-200"}`}
                  >
                    Inactive
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Refresh Button */}
          <button
            onClick={() => fetchStaff()}
            disabled={loading}
            className="h-12 rounded-xl border border-gray-200 bg-white px-4 font-medium text-navy-700 transition-all hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </Card>

      {/* Toast */}
      {toast.show && (
        <div
          className={`rounded-xl p-4 ${
            toast.type === "success"
              ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Staff Table */}
      <StaffTable
        data={tableData}
        loading={loading}
        page={page}
        limit={limit}
        total={total}
        onPageChange={setPage}
        onLimitChange={setLimit}
        onEdit={handleEdit}
        onPermissions={handlePermissions}
        onDelete={handleDelete}
        canEdit={canEditOrManage}
        canDelete={canDeleteStaff}
      />
      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white dark:bg-navy-800 rounded-lg shadow-2xl p-6 max-w-sm w-full mx-4 border border-gray-100 dark:border-navy-700">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-navy-700 dark:text-white">Delete staff member</h3>
            </div>

            <div className="mb-6">
              <p className="text-gray-600 dark:text-gray-400">
                Are you sure you want to delete <span className="font-semibold text-navy-700 dark:text-white">{deleteConfirm.name}</span>? This action cannot be undone.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelDelete();
                }}
                disabled={deleteLoading}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleConfirmDelete();
                }}
                disabled={deleteLoading}
                className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffManagement;
